import React, { useCallback, useEffect, useState } from "react";
import { FiSave, FiRotateCcw, FiX, FiDatabase, FiClock, FiMap } from "react-icons/fi";
import Swal from "sweetalert2";
import moment from "moment";

import { BrComerceApiService } from "../../../../../../services/BrComerceApiService";
import { Loading } from "../../../../../../components/Loading";

import styles from "./styles.module.scss";

const ZoneSnapshotModal = ({ open, onClose }) => {
  const api = new BrComerceApiService();

  const [loading, setLoading] = useState(false);
  const [snapshots, setSnapshots] = useState([]);

  const loadSnapshots = useCallback(async () => {
    try {
      const response = await api.makeHttpRequest({
        method: "GET",
        url: "/zone-snapshots",
      });
      setSnapshots(response || []);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    if (open) loadSnapshots();
  }, [open]);

  const handleSnapshot = useCallback(async () => {
    const confirm = await Swal.fire({
      icon: "question",
      title: "Salvar snapshot?",
      text: "Isso vai salvar o estado atual das zonas da Traccar no DynamoDB.",
      showCancelButton: true,
      confirmButtonText: "Sim, salvar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#3C7DD9",
    });

    if (!confirm.value) return;

    setLoading(true);
    try {
      const response = await api.makeHttpRequest({
        method: "POST",
        url: "/zone-snapshots/snapshot",
      });

      Swal.fire({
        icon: "success",
        title: "Snapshot salvo",
        text: `${response.zoneCount} zonas salvas para ${response.snapshotDate}`,
        timer: 2000,
        showConfirmButton: false,
      });

      await loadSnapshots();
    } catch (err) {
      const errorMsg = err?.response?.data?.message || "Erro ao salvar snapshot.";
      Swal.fire({ icon: "error", title: "Erro", text: errorMsg });
    } finally {
      setLoading(false);
    }
  }, [loadSnapshots]);

  const handleRollback = useCallback(async (snapshotDate, snapshotTime) => {
    const confirm = await Swal.fire({
      icon: "warning",
      title: "Restaurar zonas?",
      html: `Isso vai <strong>substituir todas as zonas</strong> na Traccar pelo snapshot de <strong>${snapshotDate}</strong>.<br/><br/>Essa ação não pode ser desfeita sem um novo snapshot.`,
      showCancelButton: true,
      confirmButtonText: "Sim, restaurar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#d33",
    });

    if (!confirm.value) return;

    setLoading(true);
    try {
      const response = await api.makeHttpRequest({
        method: "POST",
        url: "/zone-snapshots/rollback",
        data: { snapshotDate, snapshotTime },
      });

      Swal.fire({
        icon: "success",
        title: "Rollback concluído",
        html: `Deletadas: ${response.deleted.success}<br/>Criadas: ${response.created.success}<br/>Falhas: ${response.deleted.failed + response.created.failed}`,
        timer: 3000,
        showConfirmButton: false,
      });

      await loadSnapshots();
    } catch (err) {
      const errorMsg = err?.response?.data?.message || "Erro ao restaurar zonas.";
      Swal.fire({ icon: "error", title: "Erro", text: errorMsg });
    } finally {
      setLoading(false);
    }
  }, [loadSnapshots]);

  if (!open) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <Loading loading={loading} />

        <div className={styles.modalHeader}>
          <div className={styles.modalTitle}>
            <FiDatabase size={18} />
            <h3>Snapshots de Zonas</h3>
          </div>
          <button className={styles.closeBtn} onClick={onClose}>
            <FiX size={18} />
          </button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.saveSection}>
            <p className={styles.sectionDesc}>
              Salve o estado atual das zonas da Traccar como backup.
              O job automático salva diariamente às 5h.
            </p>
            <button className={styles.saveBtn} onClick={handleSnapshot} disabled={loading}>
              <FiSave size={14} />
              Salvar Snapshot Agora
            </button>
          </div>

          <div className={styles.snapshotsList}>
            <p className={styles.sectionTitle}>Snapshots disponíveis</p>
            {snapshots.length === 0 && (
              <p className={styles.emptyText}>Nenhum snapshot encontrado.</p>
            )}
            {snapshots.map((snap, idx) => (
              <div key={`${snap.snapshotDate}-${snap.snapshotTime}-${idx}`} className={styles.snapshotItem}>
                <div className={styles.snapshotInfo}>
                  <span className={styles.snapshotDate}>
                    <FiClock size={12} />
                    {moment(snap.createdAt).subtract(3, "hours").format("DD/MM/YYYY [às] HH:mm")}
                  </span>
                  <span className={styles.snapshotZones}>
                    <FiMap size={12} />
                    {snap.zoneCount} zonas
                  </span>
                </div>
                <button
                  className={styles.rollbackBtn}
                  onClick={() => handleRollback(snap.snapshotDate, snap.snapshotTime)}
                  disabled={loading}
                  title="Restaurar este snapshot na Traccar"
                >
                  <FiRotateCcw size={12} />
                  Restaurar
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export { ZoneSnapshotModal };
